import { Directive, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import {
  BusquedaCentroTrabajoDialogComponent,
  CentroTrabajoSeleccionado
} from '../common/busquedas/busqueda-centro-trabajo-dialog.component';
import {
  BusquedaProductoDialogComponent,
  ProductoSeleccionado
} from '../common/busquedas/busqueda-producto-dialog.component';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';

export interface OpcionOficina {
  label: string;
  value: number;
}

/**
 * Base compartida para los listados de "Información Empresa" que filtran por
 * año, oficina, producto y centro de trabajo (Citas Programadas, Informes
 * Entregados, Ordenado por Consultoría y Servicios Entregados).
 *
 * Concentra los signals, computed y métodos idénticos en esos componentes
 * para reducir duplicación. Cada subclase implementa `consultar` con su
 * propio llamado al repositorio y su propia navegación a detalle.
 */
@Directive()
export abstract class InformacionEmpresaListadoBaseComponent<TItem>
  implements OnInit
{
  protected readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  protected readonly oficinaRepository = inject(OficinaRepository);
  protected readonly usuarioState = inject(UsuarioStateService);

  readonly items = signal<TItem[]>([]);
  readonly loading = signal(false);
  readonly totalRecords = signal(0);
  readonly first = signal(0);
  readonly rows = signal(10);
  readonly oficinas = signal<OpcionOficina[]>([]);
  readonly mensaje = signal('');
  readonly mensajeTipo = signal<'info' | 'warning' | 'error'>('info');

  filtroAno = `${new Date().getFullYear()}`;
  filtroOficina: number | null = null;
  protected oficinaSesion: number | null = null;

  mostrarBuscadorProducto = false;
  readonly productoSeleccionado = signal<ProductoSeleccionado | null>(null);

  mostrarBuscadorCentroTrabajo = false;
  readonly centroTrabajoSeleccionado = signal<CentroTrabajoSeleccionado | null>(
    null
  );

  readonly nombreCliente = computed(
    () =>
      this.route.snapshot.queryParamMap.get('nombreBeneficiario') ??
      'Sin cliente'
  );

  readonly nombreConsultor = computed(
    () =>
      this.route.snapshot.queryParamMap.get('nombreConsultor') ??
      'Sin consultor'
  );

  readonly idBeneficiario = computed(() =>
    this.toNumber(
      this.route.snapshot.queryParamMap.get('nmconsecutivoBeneficiario')
    )
  );

  readonly idAsesor = computed(() =>
    this.toNumber(
      this.route.snapshot.queryParamMap.get('nmconsecutivoConsultor')
    )
  );

  readonly productoSeleccionadoLabel = computed(() => {
    const producto = this.productoSeleccionado();
    if (!producto) {
      return '';
    }
    return `${producto.cdproducto} - ${producto.dsproducto}`;
  });

  readonly centroTrabajoSeleccionadoLabel = computed(() => {
    const centro = this.centroTrabajoSeleccionado();
    if (!centro) {
      return '';
    }
    return centro.descripcionCompleta;
  });

  readonly resumenContexto = computed(
    () => `Cliente: ${this.nombreCliente()} | Consultor: ${this.nombreConsultor()}`
  );

  readonly queryParamsRegreso = this.route.snapshot.queryParams;

  readonly mensajeClass = computed(() => {
    if (this.mensajeTipo() === 'error') {
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
    }
    if (this.mensajeTipo() === 'warning') {
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
    }
    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
  });

  ngOnInit(): void {
    void this.inicializar();
  }

  protected async inicializar(): Promise<void> {
    await this.precargarSesionYOficinas();
    await this.buscar();
  }

  async buscar(): Promise<void> {
    if (!this.validarFiltros()) {
      return;
    }
    this.first.set(0);
    await this.consultar(0, this.rows());
  }

  async onPageChange(event: PaginatorState): Promise<void> {
    const first = event.first ?? this.first();
    const rows = event.rows ?? this.rows();
    this.first.set(first);
    this.rows.set(rows);

    if (!this.validarFiltros(false)) {
      return;
    }

    await this.consultar(first, rows);
  }

  limpiarFiltros(): void {
    this.filtroAno = `${new Date().getFullYear()}`;
    this.filtroOficina = this.oficinaSesion;
    this.productoSeleccionado.set(null);
    this.centroTrabajoSeleccionado.set(null);
    this.items.set([]);
    this.totalRecords.set(0);
    this.first.set(0);
    this.mensaje.set('');
  }

  onAnoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valorNumerico = input.value.replace(/\D/g, '').slice(0, 4);
    this.filtroAno = valorNumerico;
    input.value = valorNumerico;
  }

  abrirBusquedaProducto(): void {
    this.mostrarBuscadorProducto = true;
  }

  cerrarBusquedaProducto(): void {
    this.mostrarBuscadorProducto = false;
  }

  onProductoSeleccionado(producto: ProductoSeleccionado): void {
    this.productoSeleccionado.set(producto);
    this.mostrarBuscadorProducto = false;
  }

  limpiarProducto(): void {
    this.productoSeleccionado.set(null);
  }

  abrirBusquedaCentroTrabajo(): void {
    if (!this.idBeneficiario()) {
      this.mensajeTipo.set('warning');
      this.mensaje.set(
        'Debes seleccionar un cliente para buscar centro de trabajo.'
      );
      return;
    }
    this.mostrarBuscadorCentroTrabajo = true;
  }

  cerrarBusquedaCentroTrabajo(): void {
    this.mostrarBuscadorCentroTrabajo = false;
  }

  onCentroTrabajoSeleccionado(centro: CentroTrabajoSeleccionado): void {
    this.centroTrabajoSeleccionado.set(centro);
    this.mostrarBuscadorCentroTrabajo = false;
  }

  limpiarCentroTrabajo(): void {
    this.centroTrabajoSeleccionado.set(null);
  }

  protected async precargarSesionYOficinas(): Promise<void> {
    const [usuario, oficinasData] = await Promise.all([
      this.usuarioState.cargar(),
      this.oficinaRepository.obtenerOficinas()
    ]);

    this.oficinas.set(
      oficinasData.map((oficina) => ({
        value: oficina.nmconsecutivo,
        label: oficina.dsoficina
      }))
    );

    const oficinaUsuario = usuario?.nmconsecOficina ?? null;
    this.oficinaSesion = oficinaUsuario;
    this.filtroOficina = oficinaUsuario;
  }

  protected validarFiltros(mostrarError = true): boolean {
    const anoValido = /^\d{4}$/.test(this.filtroAno);

    if (!anoValido || !this.filtroOficina) {
      if (mostrarError) {
        this.mensajeTipo.set('warning');
        this.mensaje.set('Año y oficina son obligatorios.');
      }
      return false;
    }

    return true;
  }

  protected toNumber(value: string | null): number | null {
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  protected abstract consultar(first: number, rows: number): Promise<void>;
}

export { BusquedaCentroTrabajoDialogComponent, BusquedaProductoDialogComponent };
